import React, { useEffect, useState } from 'react';
import { parseWorkbook, planProject, createProject, getSession, logout, UnauthorizedError } from './api';
import TaskEditor from './components/TaskEditor';
import ProjectSettings from './components/ProjectSettings';
import SummaryPanel from './components/SummaryPanel';
import Login from './components/Login';

// Flow: drop the workbook → it's parsed into modules/tasks/activities → edit anything
// → create. The parsed quote is the single source of truth; edits mutate it, and the
// create call sends exactly what's on screen.

const newOperationKey = () =>
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

function Dropzone({ onFile, busy, error }) {
  const [over, setOver] = useState(false);

  const take = (file) => {
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) return;
    onFile(file);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files[0]); }}
      className={`rounded-xl border-2 border-dashed p-10 text-center transition-colors ${
        over ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-white'
      }`}
    >
      <p className="text-sm text-gray-600">
        {busy ? 'Reading workbook…' : 'Drop the quote workbook here'}
      </p>
      <p className="mt-1 text-xs text-gray-400">or</p>
      <label className="mt-2 inline-block cursor-pointer rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700">
        Choose file
        <input
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={(e) => take(e.target.files[0])}
        />
      </label>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [quote, setQuote] = useState(null);
  const [options, setOptions] = useState({ templateId: 'IMPACUBUS', startDate: '' });
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [serverPlan, setServerPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [operationKey, setOperationKey] = useState(newOperationKey);

  const blockers = [];
  if (quote) {
    if (!options.customerId) blockers.push('Select an Acumatica customer');
    if (!options.owner) blockers.push('Select an activity owner');
    if (!options.startDate) blockers.push('Set a project start date');
    const tasks = quote.modules.flatMap((m) => m.tasks);
    const unmapped = tasks.filter((t) => !t.taskId);
    if (unmapped.length) blockers.push(`${unmapped.length} task(s) have no Acumatica Task ID`);
    const invalidBilling = tasks.filter((t) => !['FIXED', 'TMR'].includes(t.billingRule));
    if (invalidBilling.length) blockers.push(`${invalidBilling.length} task(s) have an invalid billing rule`);
    const invalidActivities = tasks.flatMap((t) => t.activities || [])
      .filter((a) => Number(a.hours) > 0 && !String(a.summary || '').trim());
    if (invalidActivities.length) blockers.push(`${invalidActivities.length} activity description(s) are empty`);
  }

  // Ask the server whether the cookie is still good, rather than assuming from
  // localStorage — the session can expire while the tab sits open.
  useEffect(() => {
    getSession()
      .then((s) => setUser(s.authenticated ? s.username : null))
      .catch(() => setUser(null))
      .finally(() => setCheckingSession(false));
  }, []);

  // A 401 anywhere means the session lapsed: drop everything and show the login.
  const handleApiError = (e) => {
    if (e instanceof UnauthorizedError) {
      setUser(null);
      setQuote(null);
      setResult(null);
      setError(null);
      return;
    }
    setError(e.message);
  };

  // Preview the exact server-generated plan. This is the same plan the create route
  // applies, so the sidebar cannot drift from backend mapping logic.
  useEffect(() => {
    if (!user || !quote || blockers.length) { setServerPlan(null); return undefined; }
    let cancelled = false;
    setPlanBusy(true);
    const timer = setTimeout(() => {
      planProject(quote, options)
        .then((plan) => { if (!cancelled) { setServerPlan(plan); setError(null); } })
        .catch((e) => { if (!cancelled) handleApiError(e); })
        .finally(() => { if (!cancelled) setPlanBusy(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
    // The quote/options objects change whenever an editable value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, quote, options]);

  const handleSignOut = async () => {
    try { await logout(); } catch { /* signing out locally is what matters */ }
    setUser(null);
    setQuote(null);
    setResult(null);
    setServerPlan(null);
    setError(null);
  };

  const handleFile = async (file) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const parsed = await parseWorkbook(file);
      setQuote(parsed);
      setOptions((o) => ({ ...o, customerId: null }));
      setServerPlan(null);
      setOperationKey(newOperationKey());
    } catch (e) {
      handleApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async (retry = false) => {
    setCreating(true);
    setError(null);
    try {
      setResult(await createProject(quote, options, operationKey, retry));
    } catch (e) {
      handleApiError(e);
    } finally {
      setCreating(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 font-sans">
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }

  if (!user) return <Login onSignedIn={setUser} />;

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 py-4">
          <h1 className="text-lg font-semibold text-gray-900">Acacia Quote Builder</h1>
          {quote && (
            <>
              <span className="text-gray-300">/</span>
              <span className="text-sm text-gray-500">{quote.source?.file}</span>
              <button
                type="button"
                onClick={() => { setQuote(null); setResult(null); setServerPlan(null); setOperationKey(newOperationKey()); }}
                className="ml-auto rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100"
              >
                Start over
              </button>
            </>
          )}
          <div className={`flex items-center gap-3 ${quote ? '' : 'ml-auto'}`}>
            <span className="text-sm text-gray-400">{user}</span>
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-6">
        {!quote ? (
          <div className="mx-auto max-w-2xl">
            <Dropzone onFile={handleFile} busy={busy} error={error} />
          </div>
        ) : (
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="min-w-0 flex-1 space-y-6">
              <ProjectSettings
                quote={quote}
                options={options}
                onChange={(next) => {
                  setOptions(next);
                  setResult(null);
                  setOperationKey(newOperationKey());
                }}
              />
              <TaskEditor
                modules={quote.modules}
                onChange={(modules) => {
                  setQuote({ ...quote, modules });
                  setResult(null);
                  setOperationKey(newOperationKey());
                }}
              />
            </div>

            <aside className="w-full lg:w-80 lg:shrink-0">
              <SummaryPanel
                quote={quote}
                options={options}
                blockers={blockers}
                warnings={[...(quote.warnings || []), ...(error ? [error] : [])]}
                onCreate={handleCreate}
                creating={creating}
                result={result}
                serverPlan={serverPlan}
                planBusy={planBusy}
                onRetry={() => handleCreate(true)}
              />
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
